import React, { useCallback, useEffect, useState } from "react";
import { Star } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

const Stars = ({ value = 0, size = 14, interactive = false, onChange, className = "" }) => {
  const stars = [1, 2, 3, 4, 5];
  const [hover, setHover] = useState(0);
  const display = interactive ? hover || value : value;
  return (
    <div className={`inline-flex items-center gap-0.5 ${className}`}>
      {stars.map((s) => {
        const filled = display >= s - 0.4;
        return (
          <button
            type="button"
            key={s}
            disabled={!interactive}
            onMouseEnter={() => interactive && setHover(s)}
            onMouseLeave={() => interactive && setHover(0)}
            onClick={() => interactive && onChange && onChange(s)}
            className={`${interactive ? "cursor-pointer" : "cursor-default"}`}
            aria-label={`${s} star${s > 1 ? "s" : ""}`}
            data-testid={interactive ? `rating-star-${s}` : undefined}
          >
            <Star
              size={size}
              strokeWidth={1.4}
              className={filled ? "fill-ink text-ink" : "text-mute"}
            />
          </button>
        );
      })}
    </div>
  );
};

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return "";
  }
};

const Reviews = ({ productSlug = "pritch-grip-sock" }) => {
  const [data, setData] = useState({ items: [], average: 0, count: 0, distribution: {} });
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", rating: 5, title: "", comment: "" });
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/products/${productSlug}/reviews`);
      setData(res.data);
    } catch (err) {
      console.warn("[reviews] failed to load", err);
    }
  }, [productSlug]);

  useEffect(() => {
    load();
  }, [load]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.comment) return;
    setSubmitting(true);
    try {
      await api.post(`/products/${productSlug}/reviews`, form);
      toast.success("Thanks — your review is live.");
      setForm({ name: "", rating: 5, title: "", comment: "" });
      setShowForm(false);
      load();
    } catch {
      toast.error("Couldn't submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const dist = data.distribution || {};
  const total = Math.max(data.count, 1);

  return (
    <section className="container-x py-16" data-testid="reviews-section">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-4">
          <p className="overline mb-4">Reviews</p>
          <h2 className="font-display uppercase text-3xl md:text-4xl tracking-tight font-bold leading-[1]">
            What players are saying.
          </h2>
          <div className="mt-6 flex items-center gap-3">
            <span className="font-display text-5xl tracking-tight font-bold leading-none">
              {data.average ? data.average.toFixed(1) : "—"}
            </span>
            <div>
              <Stars value={data.average} size={16} />
              <p className="mono text-xs text-mute mt-1">
                {data.count} review{data.count === 1 ? "" : "s"}
              </p>
            </div>
          </div>
          <div className="mt-6 space-y-1.5">
            {[5, 4, 3, 2, 1].map((r) => {
              const n = dist[r] || 0;
              const pct = (n / total) * 100;
              return (
                <div key={r} className="flex items-center gap-3 text-xs text-mute mono">
                  <span className="w-3">{r}</span>
                  <div className="flex-1 h-1.5 bg-line">
                    <div
                      className="h-full bg-ink"
                      style={{ width: `${pct}%` }}
                      data-testid={`dist-bar-${r}`}
                    />
                  </div>
                  <span className="w-8 text-right">{n}</span>
                </div>
              );
            })}
          </div>
          <button
            type="button"
            onClick={() => setShowForm((v) => !v)}
            className="btn-secondary mt-8 w-full lg:w-auto"
            data-testid="reviews-write-button"
          >
            {showForm ? "Cancel" : "Write a review"}
          </button>

          {showForm && (
            <form onSubmit={onSubmit} className="mt-6 bg-white border border-line p-5 space-y-4" data-testid="reviews-form">
              <div>
                <label className="overline block mb-2">Name</label>
                <input
                  required
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full border border-line px-3 py-2 bg-chalk focus:outline-none focus:border-ink text-sm"
                  data-testid="review-form-name"
                />
              </div>
              <div>
                <label className="overline block mb-2">Rating</label>
                <Stars
                  value={form.rating}
                  size={22}
                  interactive
                  onChange={(r) => setForm({ ...form, rating: r })}
                />
              </div>
              <div>
                <label className="overline block mb-2">Title (optional)</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className="w-full border border-line px-3 py-2 bg-chalk focus:outline-none focus:border-ink text-sm"
                  data-testid="review-form-title"
                />
              </div>
              <div>
                <label className="overline block mb-2">Comment</label>
                <textarea
                  required
                  rows={4}
                  value={form.comment}
                  onChange={(e) => setForm({ ...form, comment: e.target.value })}
                  className="w-full border border-line px-3 py-2 bg-chalk focus:outline-none focus:border-ink text-sm"
                  data-testid="review-form-comment"
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="btn-primary w-full"
                data-testid="review-form-submit"
              >
                {submitting ? "Posting…" : "Post review"}
              </button>
            </form>
          )}
        </div>

        <div className="lg:col-span-8">
          {data.items.length === 0 ? (
            <p className="text-mute">Be the first to share a matchday verdict.</p>
          ) : (
            <ul className="divide-y divide-line border-y border-line">
              {data.items.map((r) => (
                <li key={r.id} className="py-6" data-testid={`review-${r.id}`}>
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <Stars value={r.rating} size={14} />
                      {r.verified_purchase && (
                        <span className="mono text-[10px] tracking-widest uppercase px-2 py-0.5 border border-line text-mute">
                          Verified
                        </span>
                      )}
                    </div>
                    <span className="mono text-xs text-mute">{formatDate(r.created_at)}</span>
                  </div>
                  {r.title && (
                    <h3 className="mt-3 font-display uppercase text-lg tracking-tight font-bold">
                      {r.title}
                    </h3>
                  )}
                  <p className="mt-2 text-sm leading-relaxed">{r.comment}</p>
                  <p className="mt-3 mono text-xs text-mute">— {r.name}</p>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
};

export { Stars };
export default Reviews;
